import type { AddLabel, RemoveLabel } from '@trello/action-history';
import { featureFlagClient } from '@trello/feature-flag-client';
import { client } from '@trello/graphql';
import { forNamespace } from '@trello/i18n';
import type { Card } from '@trello/model-types';

import type { Card as LegacyCardModel } from 'app/scripts/models/Card';
import type { Label } from 'app/scripts/models/Label';
import type { Trace } from '../ActionMapTypes';
import type { CardActionFragment } from '../CardActionFragment.generated';
import { CardActionFragmentDoc } from '../CardActionFragment.generated';
import { CardActionDocument } from '../CardActionMutation.generated';
import { getCached } from '../getCached';
import { LabelNameFragmentDoc } from '../LabelNameFragment.generated';
import { NoopError } from '../NoopError';
import { recordCardAction } from '../recordCardAction';

const format = forNamespace('notificationsGrouped', {
  shouldEscapeStrings: false,
});

export const addLabel = (
  card: LegacyCardModel,
  action: AddLabel,
  trace: Trace,
) => {
  const useGraphQlActions = featureFlagClient.get(
    'xf.undo-redo-rewrite',
    false,
  );

  const cardId = card.id;
  const labelId = action.idLabel;
  const { traceId } = trace;

  /**
   * keeping this here since we need it in the else block,
   * but will be converted to a fragment after rollout
   */
  const label = getCached('Label', action.idLabel) as Label;

  if (useGraphQlActions) {
    const cardFragment = client.readFragment<CardActionFragment>({
      id: `Card:${cardId}`,
      fragment: CardActionFragmentDoc,
    });

    if (!cardFragment || cardFragment.idLabels.includes(labelId)) {
      throw new NoopError();
    }

    const newLabels = [...cardFragment.idLabels, labelId];

    recordCardAction(cardId, action);

    // don't await so we don't block the UI
    client.mutate({
      mutation: CardActionDocument,
      variables: {
        cardId,
        card: {
          idLabels: newLabels,
        },
        traceId,
      },
      optimisticResponse: {
        __typename: 'Mutation',
        updateCard: {
          ...cardFragment,
          __typename: 'Card',
          id: cardId,
          idLabels: newLabels,
        },
      },
      update(cache, { data: optimisticData }) {
        if (!optimisticData) {
          return;
        }
        cache.modify<Card>({
          id: cache.identify({
            id: cardId,
            __typename: 'Card',
          }),
          fields: {
            labels(existingLabels = []) {
              if (!Array.isArray(existingLabels)) {
                return existingLabels;
              }
              return [
                ...existingLabels,
                {
                  __ref: cache.identify({
                    id: labelId,
                    __typename: 'Label',
                  }),
                },
              ];
            },
          },
        });
      },
    });
  } else {
    if (card.hasLabel(label)) {
      throw new NoopError();
    }
    card.toggleLabel(label, true);
  }

  const labelFragment = client.readFragment({
    id: `Label:${labelId}`,
    fragment: LabelNameFragmentDoc,
  });

  return format('notification_added_label_to_card', {
    label: labelFragment?.name,
  });
};

export const removeLabel = (
  card: LegacyCardModel,
  action: RemoveLabel,
  trace: Trace,
) => {
  const useGraphQlActions = featureFlagClient.get(
    'xf.undo-redo-rewrite',
    false,
  );

  const cardId = card.id;
  const labelId = action.idLabel;
  const { traceId } = trace;
  const label = getCached('Label', action.idLabel) as Label;

  if (useGraphQlActions) {
    const cardFragment = client.readFragment<CardActionFragment>({
      id: `Card:${cardId}`,
      fragment: CardActionFragmentDoc,
    });

    if (!cardFragment || !cardFragment.idLabels.includes(labelId)) {
      throw new NoopError();
    }

    const newLabels = cardFragment.idLabels.filter(
      (id: string) => id !== labelId,
    );

    recordCardAction(cardId, action);

    // don't await so we don't block the UI
    client.mutate({
      mutation: CardActionDocument,
      variables: {
        cardId,
        card: {
          idLabels: newLabels,
        },
        traceId,
      },
      optimisticResponse: {
        __typename: 'Mutation',
        updateCard: {
          ...cardFragment,
          __typename: 'Card',
          id: cardId,
          idLabels: newLabels,
        },
      },
      update(cache, { data: optimisticData }) {
        if (!optimisticData) {
          return;
        }
        cache.modify<Card>({
          id: cache.identify({
            id: cardId,
            __typename: 'Card',
          }),
          fields: {
            labels(existingLabels = [], { readField }) {
              const updatedLabels = existingLabels.filter((labelRef) => {
                return readField('id', labelRef) !== labelId;
              });
              return updatedLabels;
            },
          },
        });
      },
    });
  } else {
    if (!card.hasLabel(label)) {
      throw new NoopError();
    }
    card.toggleLabel(label, false);
  }

  const labelFragment = client.readFragment({
    id: `Label:${labelId}`,
    fragment: LabelNameFragmentDoc,
  });

  return format('notification_removed_label_from_card', {
    label: labelFragment.name,
  });
};
